// SceneTile.js
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './SceneTile.css';

const SceneTile = ({
  image,
  title,
  duration,
  summary,
  additionalInfo,
  onRegenerateImage,
  onRegenerateImagePostEdit,
  onGenerateVideo,
  isVideoGenerating,
  videoGeneratingDots,
  isImageRegenerating = false,    // NEW prop
  compact = false,                // ← NEW (defaults to false; preserves current behavior)
  // Controlled props from parent (NarrativeWorkbench)
  showHistory = false,
  activeMode = 'image', // 'image' or 'video'
  imageHistory = [],
  onSelectImage,
  imageModels = [],
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // {x, y} or null
  const [isEditing, setIsEditing] = useState(false);
  const [visualPrompt, setVisualPrompt] = useState(additionalInfo.visual);
  const [sceneImage, setSceneImage] = useState(image);
  const [audioUrls, setAudioUrls] = useState([]);
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => { setSceneImage(image); }, [image]);
  useEffect(() => {
    const urls = additionalInfo.dialogues.map(dialogue => dialogue.audio);
    setAudioUrls(urls);
  }, [additionalInfo.dialogues]);


  // Build slides: cover image first, then all shot images (chronological: beat order, then shot order)
  const slides = useMemo(() => {
    const cover = image ? [{ url: image, label: 'Cover' }] : [];
    const matrix = Array.isArray(additionalInfo?.shotMatrix) ? additionalInfo.shotMatrix : [];
    const shotImgs = [];
    matrix
      .slice()
      .sort((a, b) => (a?.beat ?? 0) - (b?.beat ?? 0))
      .forEach(row => {
        const shots = Array.isArray(row?.shots) ? row.shots : [];
        shots
          .slice()
          .sort((a, b) => {
            // prefer explicit order by id if numeric-ish; otherwise keep original
            const ai = Number.isFinite(+a?.id) ? +a.id : 0;
            const bi = Number.isFinite(+b?.id) ? +b.id : 0;
            return ai - bi;
          })
          .forEach(sh => {
            if (sh?.imageURL) {
              const label = `Beat ${row?.beat ?? ''} • Shot ${sh?.id ?? ''}`;
              shotImgs.push({ url: sh.imageURL, label });
            }
          });
      });
    const all = [...cover, ...shotImgs];
    // keep slideIndex valid if slides shrink/expand
    if (all.length && slideIndex >= all.length) setSlideIndex(0);
    return all.length ? all : cover; // fallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image, additionalInfo?.shotMatrix]);

  const goPrev = (e) => { e?.stopPropagation?.(); setSlideIndex(i => Math.max(0, i - 1)); };
  const goNext = (e) => { e?.stopPropagation?.(); setSlideIndex(i => Math.min((slides.length - 1), i + 1)); };
  const atStart = slideIndex <= 0;
  const atEnd = slideIndex >= Math.max(0, slides.length - 1);


  const handleToggleDetails = () => setShowDetails(!showDetails);
  const handleRegenerateImage = async () => onRegenerateImage(null); // Explicitly null for no override
  const handleVisualPromptClick = () => setIsEditing(true);
  const handleVisualPromptChange = (e) => setVisualPrompt(e.target.value);
  const handleVisualPromptKeyDown = async (e) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      onRegenerateImagePostEdit(visualPrompt);
    }
  };

  const handleGenerateVideo = async () => { await onGenerateVideo(); };

  return (
    <>
      <div className="scene-tile">
        <div className="title-bar">
          <h3>
            {title}
            {slides?.length > 0 ? ` — ${slides[slideIndex]?.label || ''} (${slideIndex + 1}/${slides.length})` : ''}
          </h3>
          <div className="title-actions">
            {/* History button moved to Vertical Toolbar in NarrativeWorkbench */}
          </div>
        </div>

        {/* Context Menu for Models - Portaled to body to avoid overflow clipping */}
        {contextMenu && createPortal(
          <>
            <div className="scene-tile-context-backdrop" onClick={() => setContextMenu(null)} />
            <div
              className="scene-tile-context-menu"
              style={{ top: contextMenu.y, left: contextMenu.x }}
            >
              <div className="context-menu-header">Select Model</div>
              {imageModels.map(m => (
                <div
                  key={m.alias}
                  className="context-menu-item"
                  onClick={() => {
                    onRegenerateImage(m.alias); // Pass override
                    setContextMenu(null);
                  }}
                >
                  {m.label || m.alias}
                </div>
              ))}
            </div>
          </>,
          document.body
        )}


        <div className="scene-image-wrap" onClick={handleToggleDetails}>
          <img
            src={(slides[slideIndex]?.url) || sceneImage}
            alt={title}
            className={`scene-image${isImageRegenerating ? ' blur-effect' : ''}`}
          />
          {isImageRegenerating && (
            <div className="scene-tile-spinner"></div>
          )}
          {slides.length > 1 && (
            <>
              <button
                className={`nav-arrow nav-left${atStart ? ' disabled' : ''}`}
                onClick={goPrev}
                aria-label="Previous slide"
              >
                ‹
              </button>
              <button
                className={`nav-arrow nav-right${atEnd ? ' disabled' : ''}`}
                onClick={goNext}
                aria-label="Next slide"
              >
                ›
              </button>
            </>
          )}
        </div>

        {/* History Strip - Controlled by 'showHistory' prop now */}
        {
          showHistory && imageHistory?.length > 0 && (
            <div className="history-strip">
              {imageHistory.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`History ${i}`}
                  className={`history-thumb ${url === image ? 'active' : ''}`}
                  onClick={() => { onSelectImage(url); }}
                />
              ))}
            </div>
          )
        }

      </div >
    </>
  );
};

export default SceneTile;

